import { useEffect, useState, useRef, useMemo } from 'react'
import { PlusIcon } from '@heroicons/react/24/outline'
import { useAuth } from 'auth/AuthContext'
import { showAlert } from 'services/alerts.service'
import { makeRequest } from 'services/request.service'
import { getOnChangeFunction, getFormattedMissingFieldsMessage } from 'utils'
import ButtonComponent from 'components/common/ButtonComponent'
import LogoUploadComponent from 'components/common/LogoUploadComponent'
import LabeledSelectComponent from 'components/common/LabeledSelectComponent'
import LabeledInputComponent from 'components/common/LabeledInputComponent'

const selectLogoOptions = [
  { value: '1', label: 'One Time' },
  { value: '2', label: 'One Time Shared' },
  { value: '3', label: 'Regular' },
  { value: '4', label: 'Shared' },
  { value: '5', label: 'Units' },
  { value: '6', label: 'Weight (Kg)' }
]

const selectContainerTypeOptions = [
  { value: '1', label: '89 PLASTIC/9.000KG 9.00 PLASTIC' }
]

const selectCreditDisplayOptions = [
  { value: '1', label: 'Units' },
  { value: '2', label: 'Weight (Kg)' }
]

const FarmDetailsSection = ({ formData, onChangeValue, user, missingFields }) => {
  return (
    <section className='mb-6 border border-gray-200 rounded-lg p-4 bg-gray-50'>
      <h2 className='font-semibold text-greenPrimary mb-4'>Farm Details</h2>
      <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6'>

        <LabeledInputComponent
          label='First Name'
          name='firstName'
          type='text'
          onChange={onChangeValue}
          defaultValue={formData.firstName}
          disabled
        />

        <LabeledInputComponent
          label='Last Name'
          name='lastName'
          type='text'
          defaultValue={formData.lastName}
          onChange={onChangeValue}
          disabled
        />

        <LabeledInputComponent
          label='Farm Name'
          name='farmName'
          type='text'
          value={formData.farmName}
          onChange={onChangeValue}
          missingFields={missingFields}
          required
        />

        <LabeledSelectComponent
          name='timezone'
          label='Timezone'
          value={formData.timezone}
          onChange={onChangeValue}
          missingFields={missingFields}
          required
        >
          {/* {Intl.supportedValuesOf('timeZone').map(tz => (
            <option key={tz} value={tz}>{tz}</option>
          ))} */}
          {[
            'America/Mexico_City',
            'UTC',
            'America/New_York',
            'America/Los_Angeles',
            'Europe/London',
            'Europe/Berlin',
            'Asia/Tokyo',
            'Asia/Shanghai',
            'Asia/Kolkata'
          ].map(tz => (
            <option key={tz} value={tz}>{tz}</option>
          ))}
        </LabeledSelectComponent>

        <LabeledSelectComponent
          name='country'
          label='Country'
          value={formData.country}
          onChange={onChangeValue}
        >
          <option value='Mexico'>México</option>
          <option value='USA'>Estados Unidos</option>
          <option value='Canada'>Canadá</option>
        </LabeledSelectComponent>

        <LabeledInputComponent
          label='Cost Base Hour'
          name='costBaseHour'
          type='decimal'
          value={formData.costBaseHour}
          onChange={onChangeValue}
          missingFields={missingFields}
          required
        />

        <LabeledSelectComponent
          name='currency'
          label='Currency'
          value={formData.currency}
          onChange={onChangeValue}
          missingFields={missingFields}
          required
        >
          {
            ['MXN', 'EUR', 'USD', 'ILS'].map((cr) => {
              return <option key={cr} value={cr}>{cr}</option>
            })
          }
          {/* {
            Intl.supportedValuesOf('currency').map((cr) => {
              return <option key={cr} value={cr}>{cr}</option>
            })
          } */}
        </LabeledSelectComponent>

        {/* <div>
            <label className='block text-sm font-medium text-gray-700' htmlFor='definition-of-director-password'>Change Manage Protect Password *</label>
            <input
              type='password'
              id='definition-of-director-password'
              name='definitionOfDirectorPassword'
              value={formData.definitionOfDirectorPassword}
              onChange={onChangeValue}
              className='w-full mt-1 p-2 border rounded-md shadow-sm'
            />
          </div> */}

        <LabeledInputComponent
          label='Season Start Day'
          name='seasonStartDay'
          type='number'
          value={formData.seasonStartDay}
          onChange={onChangeValue}
          missingFields={missingFields}
          required
        />

        <LabeledSelectComponent
          name='seasonStartMonth'
          label='Season Start Month'
          value={formData.seasonStartMonth}
          onChange={onChangeValue}
          missingFields={missingFields}
          required
        >
          <option value='1'>January</option>
          <option value='2'>February</option>
          <option value='3'>March</option>
          <option value='4'>April</option>
          <option value='5'>May</option>
          <option value='6'>June</option>
          <option value='7'>July</option>
          <option value='8'>August</option>
          <option value='9'>September</option>
          <option value='10'>October</option>
          <option value='11'>November</option>
          <option value='12'>December</option>
        </LabeledSelectComponent>

      </div>
    </section>
  )
}

const WorkConfigurationSection = ({ formData, onChangeValue, missingFields }) => {
  return (
    <section className='mb-6 border border-gray-200 rounded-lg p-4 bg-gray-50'>
      <h2 className='font-semibold text-greenPrimary mb-4'>Work Configuration</h2>
      <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6'>
        <LabeledSelectComponent
          label='Activities Processes Stretching Policy'
          name='stretchingOfActivitiesPolicy'
          value={formData.stretchingOfActivitiesPolicy}
          onChange={onChangeValue}
          missingFields={missingFields}
          required
        >
          <option value='1'>Forward</option>
          <option value='2'>Backwards</option>
          <option value='3'>None</option>
        </LabeledSelectComponent>

        <LabeledSelectComponent
          label='Workday Source Policy'
          name='regulationsRelatedToTheWorkingDay'
          value={formData.regulationsRelatedToTheWorkingDay}
          onChange={onChangeValue}
          missingFields={missingFields}
          required
        >
          <option value='1'>Method A</option>
          <option value='2'>Method B</option>
        </LabeledSelectComponent>

        <LabeledSelectComponent
          label='Round To Default Policy'
          name='roundToDefaultPolicy'
          value={formData.roundToDefaultPolicy}
          onChange={onChangeValue}
          missingFields={missingFields}
          required
        >
          <option value='1'>No</option>
          <option value='2'>Yes</option>
        </LabeledSelectComponent>

        <LabeledSelectComponent
          label='Workday Activity Upload Policy'
          name='workdayActivityUploadPolicy'
          value={formData.workdayActivityUploadPolicy}
          onChange={onChangeValue}
          missingFields={missingFields}
          required
        >
          <option value='1'>None</option>
          <option value='2'>Start And End Only</option>
        </LabeledSelectComponent>

        <LabeledInputComponent
          label='Start Time'
          name='startTime'
          type='time'
          value={formData.startTime}
          onChange={onChangeValue}
        />

        <LabeledInputComponent
          label='End Time'
          name='endTime'
          type='time'
          value={formData.endTime}
          onChange={onChangeValue}
        />

        <LabeledInputComponent
          label='Breaks Counts'
          name='numberOfBrakes'
          type='number'
          value={formData.numberOfBrakes}
          onChange={onChangeValue}
        />

        <LabeledInputComponent
          label='Break Start Time'
          name='brakeStartTime'
          type='time'
          value={formData.brakeStartTime}
          onChange={onChangeValue}
        />

        <LabeledInputComponent
          label='Break End Time'
          name='brakeEndTime'
          type='time'
          value={formData.brakeEndTime}
          onChange={onChangeValue}
        />

        <LabeledInputComponent
          label='Irregular Threshold'
          name='irregularThreshold'
          type='text'
          value={formData.irregularThreshold}
          onChange={onChangeValue}
        />

        <LabeledSelectComponent
          name='reuseConfiguration'
          label='Reuse Configuration'
          value={formData.reuseConfiguration}
          onChange={onChangeValue}
          missingFields={missingFields}
          required
        >
          <option value='1'>Regular</option>
          <option value='2'>Shared</option>
        </LabeledSelectComponent>

      </div>
    </section>
  )
}

const LogoConfigurationSection = ({ formData, onChangeValue }) => {
  return (
    <section className='border border-gray-200 rounded-xl p-6 bg-gray-50'>
      <h2 className='font-semibold text-greenPrimary mb-4'>Logo & Miscellaneous</h2>
      <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6'>

        <LogoUploadComponent
          logo={formData.logo}
          onUpload={newLogo => onChangeValue({ target: { name: 'logo', value: newLogo } })}
        />

        <LabeledSelectComponent
          name='reuseSetting'
          label='Reuse Settings'
          value={formData.reuseSetting}
          onChange={onChangeValue}
        >
          {selectLogoOptions.map(option => (
            <option key={option.value} value={option.label}>{option.label}</option>
          ))}
        </LabeledSelectComponent>

        <LabeledSelectComponent
          name='containerId'
          label='Container Type (default)'
          value={formData.containerId}
          onChange={onChangeValue}
        >
          {selectContainerTypeOptions.map(option => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </LabeledSelectComponent>

        <LabeledSelectComponent
          name='creditDisplayOption'
          label='Credit Display Option'
          value={formData.creditDisplayOption}
          onChange={onChangeValue}
        >
          {selectCreditDisplayOptions.map(option => (
            <option key={option.value} value={option.label}>{option.label}</option>
          ))}
        </LabeledSelectComponent>

      </div>
    </section>
  )
}

const Settings = () => {
  const hasFetched = useRef(false)
  const { user, setUser } = useAuth()

  const [state, setState] = useState({ formData: {}, missingFields: [] })

  const onChangeValue = getOnChangeFunction(setState, state.formData)

  const getGeneralSettings = async () => {
    const response = await makeRequest({
      method: 'get',
      url: `/management/general-settings/${user.userId}`,
      headers: { Authorization: `Bearer ${localStorage.getItem('secret')}` }
    })

    if (response.status === 200) {
      setState(gs => ({
        ...gs,
        formData: response.data
      }))
    }
  }

  const createGeneralSettings = async (data) => {
    const result = await makeRequest({
      method: 'post',
      url: `/management/general-settings/${user.userId}`,
      headers: {
        Authorization: `Bearer ${localStorage.getItem('secret')}`
      },
      data,
      alertResult: true
    })

    if (result.status === 200) {
      setUser({ farmId: result.data.farm.farmId, ...user })
    }
  }

  const onSave = async (e) => {
    e.preventDefault()

    const requiredFields = [
      'farmName',
      'timezone',
      'costBaseHour',
      'currency',
      'seasonStartDay',
      'seasonStartMonth',
      'stretchingOfActivitiesPolicy',
      'regulationsRelatedToTheWorkingDay',
      'roundToDefaultPolicy',
      'workdayActivityUploadPolicy',
      'reuseConfiguration'
    ]

    const missingFields = requiredFields.filter(field => {
      const value = state.formData[field]
      return value === undefined || value === null || value === ''
    })

    setState(prev => ({ ...prev, missingFields }))

    if (missingFields.length > 0) {
      const htmlMessage = getFormattedMissingFieldsMessage(state.formData, requiredFields)
      showAlert({
        html: htmlMessage,
        icon: 'warning',
        focus: missingFields[0]
      })

      return
    }

    await createGeneralSettings(state.formData)
  }

  useEffect(function () {
    if (hasFetched.current) return
    hasFetched.current = true

    getGeneralSettings()
  }, [])

  // const isFieldMissing = fieldName => state.missingFields.includes(fieldName)

  const missingFieldsSet = useMemo(() => new Set(state.missingFields), [state.missingFields])

  return (
    <form autoComplete='off'>
      <FarmDetailsSection
        formData={state.formData}
        onChangeValue={onChangeValue}
        user={user}
        missingFields={missingFieldsSet}
      />

      <WorkConfigurationSection
        formData={state.formData}
        onChangeValue={onChangeValue}
        missingFields={missingFieldsSet}
      />

      <LogoConfigurationSection formData={state.formData} onChangeValue={onChangeValue} />

      <div className='sticky bottom-0 bg-white pt-4 pb-2 flex justify-end border-t border-gray-200 mt-4'>
        <ButtonComponent onClick={onSave}>
          <PlusIcon className='w-5 h-5 mr-1 text-white' />
          Save General Settings
        </ButtonComponent>
      </div>
    </form>
  )
}

export default Settings
